import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Header } from "./layout/Header.js";
import { Sidebar } from "./layout/Sidebar.js";
import { useCollapse, folderPaths, anyOpen } from "./layout/collapse.js";
import { CollapseAllIcon, CloseIcon, ExpandAllIcon } from "./icons.js";
import { SourceTree } from "./layout/SourceTree.js";
import { RightSidebar } from "./layout/RightSidebar.js";
import { useSession, useTree } from "./data/usePage.js";
import { RepoContext, repoFromPath, repoRelativePath, reposOnShow } from "./data/repo.js";
import { PageView } from "./pages/PageView.js";
import { DataView } from "./pages/DataView.js";
import { TreeView } from "./pages/TreeView.js";
import { RepoView } from "./pages/RepoView.js";
import { AppsView } from "./pages/AppsView.js";
import { AccessView } from "./pages/AccessView.js";
import { HatsView } from "./pages/HatsView.js";
import { LocalView } from "./pages/LocalView.js";
import { ToolsView } from "./pages/ToolsView.js";
import { ShareView } from "./pages/ShareView.js";
import { VssView } from "./pages/VssView.js";
import { StatusView } from "./pages/StatusView.js";
import { CleanupView } from "./pages/CleanupView.js";
import type { RepoBrief, SessionInfo } from "../shared/types.js";

/** Which sidebar is showing as an overlay drawer, on a window too narrow to
 * hold it as a column. `null` is the normal case — including every wide window,
 * where the bars are plain columns and this state is inert (the drawer CSS only
 * exists inside the narrow media queries). */
type Drawer = "files" | "rail" | null;

export function App() {
  const session = useSession();
  const location = useLocation();
  const [drawer, setDrawer] = useState<Drawer>(null);

  // A drawer is a transient overlay: navigating (tapping a file in the tree)
  // and Escape both dismiss it. Nothing watches the viewport — widening the
  // window re-parks the bar as a column via CSS, leaving this state harmless.
  useEffect(() => setDrawer(null), [location.pathname]);
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setDrawer(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  // Keep the browser-tab favicon in sync with the configured icon. The server
  // serves the asset at /favicon.svg; we re-point the <link> with a cache-bust
  // token so a config change (session refetches after a publish) re-renders the
  // tab live — a plain reload otherwise serves the aggressively-cached old icon.
  const icon = session.status === "ready" ? session.data.icon : null;
  useEffect(() => {
    if (icon === null) return; // session not loaded yet
    const href = `/favicon.svg?v=${encodeURIComponent(icon || "default")}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"][type="image/svg+xml"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/svg+xml";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) link.setAttribute("href", href);
  }, [icon]);

  // Apply the configured header height (appearance `headerHeight:`) to the CSS
  // var that sizes the header and the sticky sidebar offsets. "" → the default.
  const headerHeight = session.status === "ready" ? session.data.headerHeight : "";
  useEffect(() => {
    const root = document.documentElement.style;
    if (headerHeight) root.setProperty("--dw-header-h", headerHeight);
    else root.removeProperty("--dw-header-h");
  }, [headerHeight]);

  if (session.status === "loading") return null;
  if (session.status === "error") {
    return <div className="dw-error">Failed to load session: {session.error.message}</div>;
  }

  const repos = session.data.repos;
  const firstRepo = repos[0]?.name ?? "";
  const currentRepo = repoFromPath(location.pathname, firstRepo);
  // Every page is three-column; only the LEFT sidebar swaps — /_data shows the
  // data-source tree instead of the per-repo file accordion.
  const isData = location.pathname.startsWith("/_data");

  return (
    <RepoContext.Provider value={currentRepo}>
      <VersionWarning remote={session.data.remote} />
      <Header
        session={session.data}
        filesOpen={drawer === "files"}
        onToggleFiles={() => setDrawer((d) => (d === "files" ? null : "files"))}
      />
      <div className="dw-layout">
        <aside id="dw-files" className={drawer === "files" ? "dw-aside dw-open" : "dw-aside"}>
          {isData ? <SourceTree /> : <RepoAccordion repos={repos} currentRepo={currentRepo} />}
        </aside>
        <Main firstRepo={firstRepo} />
        <RightSidebar
          session={session.data}
          open={drawer === "rail"}
          onToggle={() => setDrawer((d) => (d === "rail" ? null : "rail"))}
        />
      </div>
      {/* Click-off for whichever drawer is open. Rendered only while one is —
          and `display: none` above the breakpoints, so a stale open state can
          never leave an invisible click-eater over a wide window. */}
      {drawer && (
        <button
          type="button"
          className="dw-drawer-backdrop"
          aria-label="close"
          onClick={() => setDrawer(null)}
        />
      )}
    </RepoContext.Provider>
  );
}

/** cwd-mode only: warns when this local `npx` client's build differs from the
 * remote it queries. The two share the data API contract, so a drift (a stale
 * tarball) silently breaks queries — show the versions + the exact re-pull
 * command. The local version comes from this server's own /healthz; the remote
 * version + mismatch flag are computed server-side (see middleware.sessionInfo). */
function VersionWarning({ remote }: { remote: SessionInfo["remote"] }) {
  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    fetch("/healthz")
      .then((res) => res.json())
      .then((data) => setLocal(data.version))
      .catch(() => {});
  }, []);

  const repull = remote
    ? `npx --registry=${remote.url}/_npm/ @datawiki/datawiki --remote ${remote.url}`
    : "";

  // Also log to the console: the banner is easy to scroll past, and a mismatch
  // is the first thing to suspect when data queries misbehave.
  useEffect(() => {
    if (remote?.mismatch && local) {
      console.warn(
        `datawiki version mismatch: local client ${local} vs remote ${remote.url} ` +
          `${remote.version}. Data queries may break — re-pull: ${repull}`,
      );
    }
  }, [remote, local, repull]);

  if (!remote?.mismatch) return null;
  return (
    <div className="dw-version-warning" role="alert">
      <strong>version mismatch</strong> — this local client is{" "}
      <code>{local ?? "?"}</code> but the remote <code>{remote.url}</code> is{" "}
      <code>{remote.version}</code>. Data queries may break. Re-pull the matching
      client: <code>{repull}</code>
    </div>
  );
}

/** Left sidebar: every *visible* content repo as an accordion section; the
 * active repo is open. Each section shows that repo's file tree (start simple —
 * every repo's tree fetches; lazy-on-expand is a later refinement).
 *
 * Hidden repos (`datawiki-config`, `dwdocs`, anything an admin marks hidden) are
 * left out — but browsing into one focuses the sidebar on it *alone*, so its
 * tree is still there to navigate. The × in its header leaves that focus. */
function RepoAccordion({
  repos,
  currentRepo,
}: {
  repos: RepoBrief[];
  currentRepo: string;
}) {
  const shown = reposOnShow(repos, currentRepo);
  // Focused = the one on show is a hidden repo we're inside, not the visible set.
  const focused = shown.length === 1 && shown[0].hidden;
  return (
    <nav className="dw-repos">
      {shown.map((r) => (
        <RepoSection
          key={r.name}
          repo={r.name}
          open={r.name === currentRepo}
          focused={focused}
        />
      ))}
    </nav>
  );
}

function RepoSection({
  repo,
  open,
  focused,
}: {
  repo: string;
  open: boolean;
  /** This is a hidden repo shown on its own — offer the × back to all repos. */
  focused: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const tree = useTree(repo);
  const files = tree.status === "ready" ? tree.data.files : [];
  // Until the tree lands, assume no: a create button that appears and then
  // vanishes is worse than one that appears a beat late.
  const canEdit = tree.status === "ready" && tree.data.can.includes("edit");
  const currentPath =
    repo === repoFromPath(location.pathname, repo)
      ? repoRelativePath(location.pathname, repo)
      : "";
  const collapse = useCollapse(repo);
  const dirs = useMemo(() => folderPaths(files), [files]);
  // One button that reflects state: if anything's open it collapses all, else
  // it expands all. preventDefault stops the click toggling the accordion.
  const expanded = anyOpen(dirs, currentPath, collapse);
  const fold = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    collapse.setAll(dirs, !expanded);
  };
  // Leaving a focused hidden repo: "/" is the default repo's home, which is what
  // brings the full accordion back. navigate() rather than a <Link> so the click
  // can be kept from toggling the <details> it sits in.
  const leave = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/");
  };
  return (
    <details className="dw-repo-section" open={open}>
      <summary className="dw-repo-summary">
        {/* The repo name goes to its content home, never to /_repo — the git
            surface is reached from the publish control and /_tools instead. */}
        <Link to={`/${repo}/`} className="dw-repo-name" title={`${repo} home`}>
          {repo}
        </Link>
        {dirs.length > 0 && (
          <button
            type="button"
            className="dw-tree-fold-btn"
            title={expanded ? "collapse all" : "expand all"}
            aria-label={expanded ? "collapse all folders" : "expand all folders"}
            onClick={fold}
          >
            {expanded ? <CollapseAllIcon /> : <ExpandAllIcon />}
          </button>
        )}
        {focused && (
          <button
            type="button"
            className="dw-tree-fold-btn"
            title="close — back to all repos"
            aria-label="close, back to all repos"
            onClick={leave}
          >
            <CloseIcon />
          </button>
        )}
      </summary>
      <Sidebar
        repo={repo}
        files={files}
        canEdit={canEdit}
        currentPath={currentPath}
        collapse={collapse}
      />
    </details>
  );
}

function Main({ firstRepo }: { firstRepo: string }) {
  return (
    <main className="dw-main">
      <Routes>
        {/* Bare root → the first (default) repo's home. */}
        <Route
          path="/"
          element={firstRepo ? <Navigate to={`/${firstRepo}/`} replace /> : null}
        />
        <Route path="/_data" element={<DataView />} />
        <Route path="/_tree" element={<TreeView />} />
        <Route path="/_repo/:repo" element={<RepoView />} />
        <Route path="/_tools" element={<ToolsView />} />
        <Route path="/_share" element={<ShareView />} />
        <Route path="/_vss" element={<VssView />} />
        <Route path="/_access" element={<AccessView />} />
        <Route path="/_status" element={<StatusView />} />
        <Route path="/_cleanup" element={<CleanupView />} />
        <Route path="/_hats" element={<HatsView />} />
        <Route path="/_apps" element={<AppsView />} />
        {/* OAuth callbacks and old links point at /_tokens — keep it redirecting. */}
        <Route path="/_tokens" element={<Navigate to="/_apps" replace />} />
        <Route path="/_local" element={<LocalView />} />
        <Route path="/*" element={<PageView />} />
      </Routes>
    </main>
  );
}
